import { useEffect, useState } from 'react'
import { ContactRelatedObjects } from 'app/components/contacts/ContactRelatedObjects'
import ContactBasicActions from 'app/components/contacts/drawer/ContactBasicActions'
import { ContactDrawerBanner } from 'app/components/contacts/drawer/ContactDrawerBanner'
import { ContactInfo } from 'app/components/contacts/drawer/ContactInfo'
import DrawerV2 from 'app/components/drawer/DrawerV2'
import Spinner from 'app/components/Spinner'
import { useContactRelatedObjects } from 'app/hooks/useContactRelatedObjects'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { LocationJson } from 'app/types/entities'
import type { TaskJson } from 'app/types/entities'
import { SearchResponse } from 'app/services/search'
import { useUser, useWorkspace } from 'app/utils'
import { useAuthStore } from 'app/stores/StoreContext'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'
import { useLocationStore } from 'app/stores/StoreContext'

type ContactDrawerProps = {
  contact?: ContactJson
  openDrawer: boolean
  isNavigating?: boolean
  highlightActivityId?: string
  onClose: () => void
  onChange: (contact: ContactJson) => void
  onTaskUpdated?: (task: TaskJson) => void
  nextContact?: () => void
  previousContact?: () => void
  onActivityAdded?: (activity: ActivityJson) => void
  /** Which tab to land on. See `ContactRelatedObjects`. */
  initialTab?: string
}
/**
 * onActivityAdded is called when an activity is added to the contact and it is used to run side effects such as refreshing the contact drawer or the task group.
 * @param param0
 * @returns
 */
export function ContactDrawer({
  contact,
  openDrawer,
  isNavigating = false,
  highlightActivityId,
  onClose,
  onChange,
  onTaskUpdated,
  nextContact,
  previousContact,
  onActivityAdded,
  initialTab,
}: ContactDrawerProps) {
  const user = useUser()
  const workspace = useWorkspace()
  const { products } = useAuthStore()
  const agentOnly = products?.includes(`AI Agent`) && !products?.includes(`CRM`)
  const [locationsById, setLocationsById] = useState<Record<string, LocationJson>>({})
  const [isContactInfoSaving, setIsContactInfoSaving] = useState(false)
  const [isAIThread, setIsAIThread] = useState(false)
  const locationStore = useLocationStore()

  const {
    activities,
    appointments,
    transactions,
    treatmentPlans,
    totals,
    handleActivityAdded,
    handleActivitiesChange,
    refreshActivities,
  } = useContactRelatedObjects(contact)

  // Keyed on the ids rather than the contact object: the object is rebuilt on
  // every render of whatever owns it, and re-running this on identity alone
  // refetched the location continuously.
  const contactId = contact?.id
  const contactLocationId = contact?.locationId
  // Read by the contact's own office id so a person never renders over another office.
  // Kept for the drawer's lifetime so switching back to a loaded office needs no spinner.
  const location = contactLocationId ? locationsById[contactLocationId] : undefined
  useEffect(() => {
    setIsAIThread(false)
    if (!contactLocationId) return
    bg(async () => {
      const result = await locationStore.fetchOne(contactLocationId)
      const data = result.data as any
      const loc = data?.location ?? data?.hits?.[0] ?? data
      if (loc) setLocationsById((loaded) => ({ ...loaded, [contactLocationId]: loc as LocationJson }))
    })
  }, [contactId, contactLocationId])

  debug(`contact`, `ContactDrawer`, contact, location)

  const isMobile = useIsMobile()

  const contactInfoNode =
    contact && location ? (
      <ContactInfo
        // The form reads its default values once, so each contact needs its own instance.
        key={contact.id}
        contact={contact}
        location={location}
        onChange={onChange}
        onSaved={refreshActivities}
        onActivityAdded={(activity) => {
          handleActivityAdded(activity)
          onActivityAdded?.(activity)
        }}
        onSaving={setIsContactInfoSaving}
        isAgentOnly={agentOnly}
        isAIThread={isAIThread}
      />
    ) : (
      <div className="flex h-full items-center justify-center">
        <Spinner size={20} />
      </div>
    )

  // On mobile the related-objects tab strip also carries the contact info as a
  // leading "Details" tab, so the whole drawer is one tabbed column.
  const relatedObjectsNode = contact ? (
    <ContactRelatedObjects
      contact={contact}
      location={location}
      onChange={onChange}
      onRemovedTask={() => {
        // no-op: data refreshed via stores
      }}
      onTaskUpdated={onTaskUpdated}
      activities={activities}
      appointments={appointments}
      transactions={transactions}
      treatmentPlans={treatmentPlans}
      totals={totals}
      onActivitiesChange={handleActivitiesChange}
      onAIThreadDetected={setIsAIThread}
      highlightActivityId={highlightActivityId}
      detailsTab={isMobile ? { name: `Details`, content: contactInfoNode } : undefined}
      initialTab={initialTab}
    />
  ) : null

  return (
    <>
      <DrawerV2
        open={openDrawer}
        title={location?.name}
        subheader={
          contact && location ? (
            <ContactBasicActions
              contact={contact}
              location={location}
              onChange={onChange}
              onSaved={refreshActivities}
              onActivityAdded={(activity) => {
                handleActivityAdded(activity)
                onActivityAdded?.(activity)
              }}
              isAgentOnly={agentOnly}
            />
          ) : undefined
        }
        banner={<ContactDrawerBanner />}
        leftContent={
          isMobile ? (
            <div
              aria-busy={isNavigating}
              className={`h-full w-full transition-opacity duration-200 ${isNavigating ? `pointer-events-none opacity-50` : `opacity-100`}`}
            >
              {relatedObjectsNode}
            </div>
          ) : (
            <div
              aria-busy={isNavigating}
              className={`w-105 px-1 transition-opacity duration-200 ${isNavigating ? `pointer-events-none opacity-50` : `opacity-100`}`}
            >
              {contactInfoNode}
            </div>
          )
        }
        rightContent={
          isMobile ? undefined : (
            <div
              aria-busy={isNavigating}
              className={`h-full w-175 transition-opacity duration-200 ${isNavigating ? `pointer-events-none opacity-50` : `opacity-100`}`}
            >
              {relatedObjectsNode}
            </div>
          )
        }
        onClose={() => (isContactInfoSaving ? null : onClose())}
        nextItem={nextContact}
        previousItem={previousContact}
      />
      {isContactInfoSaving && (
        <div className="absolute left-0 top-0 h-full w-full bg-surface-subtle/80" style={{ zIndex: 11 }}></div>
      )}
    </>
  )
}
